import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  areStringsEqualIgnoreCase, DEFAULT_LOADABLE_STATE,
  getLoadedResults,
  Loadable,
  LoadableHelper,
  LoadableStatus,
  ResetState,
  setLoadableInState,
  UsState,
  UsStateAbbreviation,
} from '@aiwyn/shared';
import { combineLatest, Observable, of } from 'rxjs';
import {
  AutoPay,
  CardFunding,
  PaymentPlanApiService,
  PaymentPlanUi,
  PaymentsApiService,
  TaxPaymentResponse,
  TaxPaymentsApiService,
} from '@aiwyn/api';
import { AuthService, isGuestUser } from '@aiwyn/auth';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import zipState from 'zip-state';
import { ConfigService, ConfigState } from '@aiwyn/config';
import {
  EngagementScheduledPaymentUi,
  HasuraScheduledPaymentUi,
  ScheduledPaymentUi,
  ScheduledPaymentUiV3,
} from '../../payments/models/scheduled-payment-ui';

import moment from 'moment';

import { DEFAULT_SETTINGS_STATE_MODEL, SettingsStateModel } from './setting-state.model';
import {
  ClearScheduledPayments,
  ClearTaxPayments,
  DeclineAutoPaymentV4,
  DeclinePaymentV3,
  DeclineRecurringPaymentV4,
  DisableAutoPayClient,
  GetPaymentPlans,
  GetScheduledPayments,
  GetTaxPayments,
  ResumeAutoPaymentV4,
  ResumePaymentV3,
  ResumeRecurringPaymentV4,
  UpdatePaymentV3,
  UpdatePaymentV4,
} from './settings.action';
import { CommonSettingsState, GetAutoPayClients } from '@aiwyn/common/settings';
import {
  EngagementScheduledPaymentModel,
  HasuraScheduledPaymentModel,
  ScheduledPaymentUiModel,
} from '../../../../api/models/scheduled-payment.models';

// Private, should not be exported or dispatched outside this state
class InferV4ScheduledPayments {
  static readonly type: string = '[SettingsState] InferV4ScheduledPayments';
}

interface SortableScheduledPaymentUi {
  data: HasuraScheduledPaymentUi | EngagementScheduledPaymentUi;
  date: number;
}

@State<SettingsStateModel>({
  name: 'settings',
  defaults: DEFAULT_SETTINGS_STATE_MODEL
})
@Injectable({
  providedIn: 'root'
})
export class SettingsState {

  @Selector()
  static getProcessingAutoPayDisable(state: SettingsStateModel): LoadableStatus {
    return state?.processingAutoPayDisable;
  }

  @Selector()
  static getLoadablePaymentPlans(state: SettingsStateModel): Loadable<PaymentPlanUi[]> {
    return state?.paymentPlans;
  }

  @Selector([SettingsState.getLoadablePaymentPlans])
  static getPaymentPlansStatus(paymentPlansLoadable: Loadable<PaymentPlanUi[]>): LoadableStatus {
    return paymentPlansLoadable?.status;
  }

  @Selector([SettingsState.getLoadablePaymentPlans])
  static getPaymentPlans(paymentPlansLoadable: Loadable<PaymentPlanUi[]>): PaymentPlanUi[] {
    return paymentPlansLoadable?.data ?? [];
  }

  @Selector()
  static getLoadableV3Payments(state: SettingsStateModel): Loadable<ScheduledPaymentUiModel[]> {
    return state?.v3Payments;
  }

  @Selector([SettingsState.getLoadableV3Payments])
  static getV3PaymentsStatus(v3PaymentsLoadable: Loadable<ScheduledPaymentUiModel[]>): LoadableStatus {
    return v3PaymentsLoadable?.status;
  }

  @Selector([SettingsState.getLoadableV3Payments])
  static getV3Payments(v3PaymentsLoadable: Loadable<ScheduledPaymentUiModel[]>): ScheduledPaymentUiModel[] {
    return v3PaymentsLoadable?.data ?? [];
  }

  @Selector()
  static getLoadableV4Payments(state: SettingsStateModel): Loadable<HasuraScheduledPaymentModel[]> {
    return state?.v4Payments;
  }

  @Selector([SettingsState.getLoadableV4Payments])
  static geV4PaymentsStatus(v4PaymentsLoadable: Loadable<HasuraScheduledPaymentModel[]>): LoadableStatus {
    return v4PaymentsLoadable?.status;
  }

  @Selector([SettingsState.getLoadableV4Payments])
  static getV4Payments(v4PaymentsLoadable: Loadable<HasuraScheduledPaymentModel[]>): HasuraScheduledPaymentModel[] {
    return v4PaymentsLoadable?.data ?? [];
  }

  @Selector()
  static getLoadableV4EngagementPayments(state: SettingsStateModel): Loadable<EngagementScheduledPaymentModel[]> {
    return state?.v4EngagementPayments;
  }

  @Selector([SettingsState.getLoadableV4EngagementPayments])
  static getV4EngagementPaymentsStatus(v4EngagementPaymentsLoadable: Loadable<EngagementScheduledPaymentModel[]>): LoadableStatus {
    return v4EngagementPaymentsLoadable?.status;
  }

  @Selector([SettingsState.getLoadableV4EngagementPayments])
  static getV4EngagementPayments(v4EngagementPaymentsLoadable: Loadable<EngagementScheduledPaymentModel[]>): EngagementScheduledPaymentModel[] {
    return v4EngagementPaymentsLoadable?.data ?? [];
  }

  @Selector()
  static getLoadableTaxPayments(state: SettingsStateModel): Loadable<TaxPaymentResponse[]> {
    return state?.taxPayments;
  }

  @Selector([SettingsState.getLoadableTaxPayments])
  static getTaxPaymentsStatus(taxPaymentsLoadable: Loadable<TaxPaymentResponse[]>): LoadableStatus {
    return taxPaymentsLoadable?.status;
  }

  @Selector([SettingsState.getLoadableTaxPayments])
  static getTaxPayments(taxPaymentsLoadable: Loadable<TaxPaymentResponse[]>): TaxPaymentResponse[] {
    return taxPaymentsLoadable?.data ?? [];
  }

  @Selector([ConfigState.isPaymentPlanV4Enabled, SettingsState.getV3Payments, SettingsState.getV4Payments, SettingsState.getV4EngagementPayments])
  static getScheduledPayments(
    paymentPlanV4Enabled: boolean,
    v3Payments: ScheduledPaymentUiModel[],
    v4Payments: HasuraScheduledPaymentModel[],
    v4EngagementPayments: EngagementScheduledPaymentModel[]
  ): ScheduledPaymentUi[] {
    if (!paymentPlanV4Enabled) {
      return (v3Payments ?? []).map(model => new ScheduledPaymentUiV3(model));
    }

    // Combine V4 hasura and engagement payments
    const hasuraUiModels: SortableScheduledPaymentUi[] = (v4Payments ?? []).map(model => ({
      data: HasuraScheduledPaymentUi.create(model),
      date: new Date(model.releaseDate).getTime(),
    }));

    const engagementUiModels: SortableScheduledPaymentUi[] = (v4EngagementPayments ?? []).map(model => ({
      data: new EngagementScheduledPaymentUi(model),
      date: new Date(model.date).getTime(),
    }));

    // Combine and sort by date
    const combinedModels = [...hasuraUiModels, ...engagementUiModels];
    combinedModels.sort((a, b) => a.date - b.date);

    return combinedModels.map(({ data }) => data);
  }

  static isMethodUsedForAutoPay(paymentMethodId: string): (autoPayClients: AutoPay[]) => boolean {
    return createSelector(
      [CommonSettingsState.getAutoPayClients],
      (autoPayClients: AutoPay[]) => (autoPayClients ?? []).some(autoPayClient => autoPayClient.active && autoPayClient.method.paymentMethodId === paymentMethodId),
    );
  }

  static isMethodUsedForEngagements(paymentMethodId: string): (autoPayClients: AutoPay[]) => boolean {
    return createSelector(
      [CommonSettingsState.getAutoPayClients],
      (autoPayClients: AutoPay[]) => (autoPayClients ?? []).some(autoPayClient => autoPayClient.active && autoPayClient.method.paymentMethodId === paymentMethodId),
    );
  }

  // static isMethodUsedForPaymentPlan(paymentMethodId: string): (paymentPlans: PaymentPlanUi[]) => boolean {
  //   return createSelector(
  //     [SettingsState.getPaymentPlans],
  //     (paymentPlans: PaymentPlanUi[]) => !!(
  //       paymentPlans.find(paymentPlan =>
  //         (paymentPlan.paymentPlan.paymentMethodId === id && paymentPlan.paymentPlan.active))
  //       || this.scheduledPaymentService.dataSourceScheduledPayments.data.find(scheduledPaymentUi =>
  //         (scheduledPaymentUi.actionDefInstanceDTO.action.paymentMethodId === id && scheduledPaymentUi.actionDefInstanceDTO.status === ('STAGED' || 'DECLINED')))
  //     )
  //   );
  // }

  constructor(
    private readonly paymentPlanApiService: PaymentPlanApiService,
    private readonly authService: AuthService,
    private readonly configService: ConfigService,
    private readonly store: Store,
    private readonly paymentsApiService: PaymentsApiService,
    private readonly taxPaymentsApiService: TaxPaymentsApiService,
  ) { }

  @Action([DisableAutoPayClient])
  disableAutoPayClient({ patchState, dispatch }: StateContext<SettingsStateModel>, { autoPayId }: DisableAutoPayClient): Observable<unknown> | void {
    if (!autoPayId) {
      return;
    }

    patchState({ processingAutoPayDisable: LoadableStatus.LOADING });

    return this.paymentPlanApiService.deactivateAutoPaymentPlan(autoPayId).pipe(
      tap(
        response => patchState({ processingAutoPayDisable: LoadableStatus.LOADED }),
        error => patchState({ processingAutoPayDisable: LoadableStatus.ERROR })
      ),
      switchMap(response => dispatch([new GetAutoPayClients(true), new ClearScheduledPayments()])),
      take(1),
    );
  }

  @Action([GetPaymentPlans])
  getPaymentPlans(
    { patchState, dispatch }: StateContext<SettingsStateModel>,
    { forceRefresh }: GetPaymentPlans,
  ): Observable<unknown> | void {
    if (!(forceRefresh || LoadableHelper.shouldRequest(this.store.selectSnapshot(SettingsState.getPaymentPlansStatus)))) {
      return;
    }

    // TODO :: consider refactoring this into the application user strategies
    return this.authService.getUser().pipe(
      switchMap(user => {
        if (isGuestUser(user)) {
          patchState({ paymentPlans: { status: LoadableStatus.LOADED, data: [] } });
          return of(undefined);
        }

        patchState({ paymentPlans: { status: LoadableStatus.LOADING } });
        return combineLatest([
          this.paymentPlanApiService.getPaymentPlans(user.getClientIdList()),
          getLoadedResults(this.store.select(CommonSettingsState.getUsStatesLoadable)),
        ]).pipe(
          map(([paymentPlans, usStates]) => paymentPlans.map(paymentPlan => this.setOptOutForPaymentPlanUis(paymentPlan, usStates))),
          setLoadableInState(patchState, 'paymentPlans'),
        );
      }),
      take(1),
    );
  }

  @Action([GetScheduledPayments])
  getScheduledPayments({ patchState, dispatch }: StateContext<SettingsStateModel>, {
    endingDate,
    forceRefresh
  }: GetScheduledPayments): Observable<unknown> {
    return combineLatest([
      this.store.select(ConfigState.getConfigsStatus),
      this.store.select(CommonSettingsState.getUsStatesStatus),
    ]).pipe(
      filter(statuses => LoadableHelper.coalesceStatus(statuses) === LoadableStatus.LOADED),
      switchMap(_ => this.store.select(ConfigState.isPaymentPlanV4Enabled)),
      switchMap(paymentPlanV4Enabled => {
        const relevantPaymentsStatus = this.store.selectSnapshot(
          paymentPlanV4Enabled ? SettingsState.geV4PaymentsStatus : SettingsState.getV3PaymentsStatus,
        );

        if (!(LoadableHelper.shouldRequest(relevantPaymentsStatus) || forceRefresh)) {
          return of([]);
        }

        // Mark the appropriate field as loading
        const relevantPaymentsField: keyof SettingsStateModel = paymentPlanV4Enabled ? 'v4Payments' : 'v3Payments';
        patchState({ [relevantPaymentsField]: { status: LoadableStatus.LOADING } });

        const processedEndingDate = SettingsState.apiDateParameters(endingDate);

        if (!paymentPlanV4Enabled) {
          return this.paymentsApiService.getV3ScheduledPayments(processedEndingDate).pipe(
            map(models => models.map(model => ({
              ...model,
              optOutOfProcessingFees: this._doesPaymentMethodHaveOptOutProcessingFee(
                model.actionDefInstanceDTO.action.paymentMethodId,
                model.actionDefInstanceDTO.action.paymentMethod,
                model.actionDefInstanceDTO.action.zip,
              ),
            }))),
            setLoadableInState(patchState, 'v3Payments'),
          );
        }

        return this.authService.getUser().pipe(
          switchMap(user => combineLatest([
            this.paymentsApiService.getV4ScheduledPayments(processedEndingDate, user.getClientIdList()),
            this.paymentsApiService.getV4EngagementScheduledPayments(processedEndingDate)
          ])),
          map(([hasuraModels, engagementModels]) => {
            const processedHasuraModels = hasuraModels.map(model => ({
              ...model,
              scheduledAutoPaymentEmails: (model.scheduledAutoPaymentEmails as unknown as string || '').split(','),
              scheduledPaymentEmails: (model.scheduledPaymentEmails as unknown as string || '').split(','),
              startDate: model.startDate?.replace(/T.*/, ''),
              endDate: model.endDate?.replace(/T.*/, ''),
              releaseDate: model.releaseDate?.replace(/T.*/, ''),
              optOutOfProcessingFees: this._doesPaymentMethodHaveOptOutProcessingFee(
                model.platformPaymentMethodId,
                model.paymentMethodType,
                model.zip,
              ),
            }));

            const processedEngagementModels = engagementModels.map(model => ({
              ...model,
              date: model.date?.replace(/T.*/, ''),
            }));

            // Combine both arrays and sort by date
            const combinedModels = [
              ...processedHasuraModels.map(model => ({ type: 'hasura' as const, data: model })),
              ...processedEngagementModels.map(model => ({ type: 'engagement' as const, data: model }))
            ];

            // Sort by date - hasura models use releaseDate, engagement models use date
            combinedModels.sort((a, b) => {
              const dateA = a.type === 'hasura' ? a.data.releaseDate : a.data.date;
              const dateB = b.type === 'hasura' ? b.data.releaseDate : b.data.date;
              return new Date(dateA).getTime() - new Date(dateB).getTime();
            });

            return {
              hasuraModels: processedHasuraModels,
              engagementModels: processedEngagementModels,
              combinedModels
            };
          }),
          tap({
            next: ({ hasuraModels, engagementModels }) => {
              patchState({
                v4Payments: { status: LoadableStatus.LOADED, data: hasuraModels },
                v4EngagementPayments: { status: LoadableStatus.LOADED, data: engagementModels },
              });
            },
            error: error => {
              patchState({
                v4Payments: { status: LoadableStatus.ERROR, data: [], error },
                v4EngagementPayments: { status: LoadableStatus.ERROR, data: [], error },
              });
            },
          }),
          // TODO :: Currently non-functional, leaving this step in the workflow though
          switchMap(_ => dispatch(new InferV4ScheduledPayments())),
        );
      }),
      take(1),
    );
  }

  private _doesPaymentMethodHaveOptOutProcessingFee(paymentMethodId: string, paymentMethodSubType: string, zip: string): boolean {
    const stateAbbrev = (zip ? zipState(zip) : undefined) as UsStateAbbreviation;
    const matchingUsState = this.store.selectSnapshot(CommonSettingsState.getUsState(stateAbbrev));

    return stateAbbrev
      && paymentMethodId.includes('pm_')
      && areStringsEqualIgnoreCase(paymentMethodSubType, CardFunding.CREDIT)
      && matchingUsState?.optOutOfProcessingFee;
  }

  /**
   * Scheduled payments are not generated on the back end for V4 payment plans, append inferred payments to the list
   *
   * NOTE :: THIS FUNCTIONALITY DOES NOT WORK 100%!!!!
   * Feature was cut before complete; however, the inference function is working with limitations. The recursive calculation is limited
   * to around 500 payments before it heaps out. A much lower limit will be needed if we want to maintain calculations in the front end.
   *
   * @param patchState
   */
  @Action([InferV4ScheduledPayments])
  inferV4ScheduledPayments({ patchState, getState }: StateContext<SettingsStateModel>): void {
    // TODO :: Once product wants this feature in, finish testing and remove
    if (true) {
      return;
    }

    patchState({
      v4Payments: {
        status: getState().v4Payments.status,
        data: [
          ...(getState()?.v4Payments?.data || []),
          ...(getState()?.paymentPlans?.data || []).reduce(
            (scheduledPayments, paymentPlan) => {
              const scheduledPayment: HasuraScheduledPaymentModel = (getState()?.v4Payments?.data || []).find(scheduledPayment => scheduledPayment.paymentPlanId === paymentPlan.paymentPlan.id);
              let startDate: Date;
              if (scheduledPayment) {
                // const dateParts: string[] = scheduledPayment.releaseDate.split('-');
                // startDate = new Date(+dateParts[0], +dateParts[1] - 1, +dateParts[2]);
                startDate = new Date(scheduledPayment.releaseDate);
              } else {
                startDate = new Date(paymentPlan.paymentPlan.startDate);
              }

              return [
                ...scheduledPayments,
                ...SettingsState.inferNextPaymentsForPlan(paymentPlan, startDate)
              ];
            },
            getState()?.v4Payments?.data || []
          )
        ]
      }
    });
  }

  @Action([DeclinePaymentV3])
  declinePaymentV3({ patchState }: StateContext<SettingsStateModel>, { id }: DeclinePaymentV3): Observable<unknown> {
    return this.paymentsApiService.declineScheduledPayment(id).pipe(
      tap(
        response => patchState({
          v3Payments: {
            status: LoadableStatus.LOADED,
            data: [...this.store.selectSnapshot(SettingsState.getV3Payments)].map(scheduledPayment =>
              scheduledPayment.actionDefInstanceDTO.id === id ?
                { ...scheduledPayment, actionDefInstanceDTO: { ...scheduledPayment.actionDefInstanceDTO, status: 'DECLINED' } } :
                scheduledPayment
            )
          }
        }),
        _ => console.error('Scheduled payment status was not successfully changed to DECLINED.'),
      )
    );
  }

  @Action([DeclineRecurringPaymentV4])
  declineRecurringPaymentV4({ patchState, dispatch }: StateContext<SettingsStateModel>, { id }: DeclineRecurringPaymentV4): Observable<unknown> {
    return this.paymentsApiService.skipScheduledPayment(id).pipe(
      tap(
        response => patchState({
          v4Payments: {
            status: LoadableStatus.LOADED,
            data: [...this.store.selectSnapshot(SettingsState.getV4Payments)].map(scheduledPayment =>
              scheduledPayment.scheduledPaymentId === id ?
                { ...scheduledPayment, status: 'DECLINED' } :
                scheduledPayment
            )
          }
        }),
        _ => console.error('Scheduled payment status was not successfully changed to DECLINED.'),
      )
      , tap(x => {
        [...this.store.selectSnapshot(SettingsState.getV4Payments)].map(scheduledPayment => {
            console.log(`comparing :: ${scheduledPayment.scheduledPaymentId} to ${id} :: ${scheduledPayment.scheduledPaymentId === id}`);
            return scheduledPayment.scheduledPaymentId === id ?
              { ...scheduledPayment, status: 'DECLINED' } :
              scheduledPayment;
          }
        );
      }),
      switchMap(paymentPlans => dispatch(new GetScheduledPayments(undefined, true))),
    );
  }

  @Action([DeclineAutoPaymentV4])
  declineAutoPaymentV4({ patchState, dispatch }: StateContext<SettingsStateModel>, { id }: DeclineAutoPaymentV4): Observable<unknown> {
    return this.paymentsApiService.skipScheduledAutoPayment(id).pipe(
      tap(
        response => patchState({
          v4Payments: {
            status: LoadableStatus.LOADED,
            data: [...this.store.selectSnapshot(SettingsState.getV4Payments)].map(scheduledPayment =>
              scheduledPayment.scheduledAutoPaymentId === id ?
                { ...scheduledPayment, status: 'DECLINED' } :
                scheduledPayment
            )
          }
        }),
        _ => console.error('Scheduled payment status was not successfully changed to DECLINED.'),
      ),
      switchMap(() => dispatch(new GetScheduledPayments(undefined, true)))
    );
  }

  @Action([ResumePaymentV3])
  resumePaymentV3({ patchState }: StateContext<SettingsStateModel>, { id }: ResumePaymentV3): Observable<unknown> {
    return this.paymentsApiService.resumeScheduledPayment(id).pipe(
      tap(
        response => patchState({
          v3Payments: {
            status: LoadableStatus.LOADED,
            data: [...this.store.selectSnapshot(SettingsState.getV3Payments)].map(scheduledPayment =>
              scheduledPayment.actionDefInstanceDTO.id === id ?
                { ...scheduledPayment, actionDefInstanceDTO: { ...scheduledPayment.actionDefInstanceDTO, status: 'STAGED' } } :
                scheduledPayment
            )
          }
        }),
        _ => console.error('Scheduled payment status was not successfully changed to STAGED.'),
      )
    );
  }

  @Action([ResumeRecurringPaymentV4])
  resumeRecurringPaymentV4({ patchState, dispatch }: StateContext<SettingsStateModel>, { id }: ResumeRecurringPaymentV4): Observable<unknown> {
    return this.paymentsApiService.resumeScheduledPaymentV4(id).pipe(
      tap(
        response => patchState({
          v4Payments: {
            status: LoadableStatus.LOADED,
            data: [...this.store.selectSnapshot(SettingsState.getV4Payments)].map(scheduledPayment =>
              scheduledPayment.scheduledPaymentId === id ?
                { ...scheduledPayment, status: 'STAGED' } :
                scheduledPayment
            )
          }
        }),
        _ => console.error('Scheduled payment status was not successfully changed to STAGED.'),
      ),
      switchMap(() => dispatch(new GetScheduledPayments(undefined, true))),
    );
  }

  @Action([ResumeAutoPaymentV4])
  resumeAutoPaymentV4({ patchState, dispatch }: StateContext<SettingsStateModel>, { id }: ResumeAutoPaymentV4): Observable<unknown> {
    return this.paymentsApiService.resumeScheduledAutoPaymentV4(id).pipe(
      tap(
        response => patchState({
          v4Payments: {
            status: LoadableStatus.LOADED,
            data: [...this.store.selectSnapshot(SettingsState.getV4Payments)].map(scheduledPayment =>
              scheduledPayment.scheduledAutoPaymentId === id ?
                { ...scheduledPayment, status: 'STAGED' } :
                scheduledPayment
            )
          }
        }),
        _ => console.error('Scheduled payment status was not successfully changed to STAGED.'),
      ),
      switchMap(() => dispatch(new GetScheduledPayments(undefined, true)))
    );
  }

  @Action([UpdatePaymentV3])
  updatePaymentV3({ dispatch }: StateContext<SettingsStateModel>, { editedScheduledPaymentPayload }: UpdatePaymentV3): Observable<unknown> {
    return this.paymentsApiService.updateScheduledPayment(editedScheduledPaymentPayload).pipe(
      switchMap(response => dispatch(new GetScheduledPayments(undefined, true))),
      take(1),
    );
  }

  @Action([UpdatePaymentV4])
  updatePaymentV4({ dispatch }: StateContext<SettingsStateModel>, { payload }: UpdatePaymentV4): Observable<unknown> {
    return this.paymentsApiService.updateScheduledPaymentV4(payload).pipe(
      switchMap(response => dispatch(new GetScheduledPayments(undefined, true))),
      take(1),
    );
  }

  @Action([ClearScheduledPayments])
  clearScheduledPayments({ patchState }: StateContext<SettingsStateModel>): void {
    patchState({
      v3Payments: DEFAULT_LOADABLE_STATE,
      v4Payments: DEFAULT_LOADABLE_STATE,
      v4EngagementPayments: DEFAULT_LOADABLE_STATE,
    });
  }

  @Action([GetTaxPayments])
  getTaxPayments(
    { patchState }: StateContext<SettingsStateModel>,
    { forceRefresh }: GetTaxPayments,
  ): Observable<unknown> | void {
    const status = this.store.selectSnapshot(SettingsState.getTaxPaymentsStatus);
    if (!(forceRefresh || LoadableHelper.shouldRequest(status))) {
      return;
    }

    patchState({ taxPayments: { status: LoadableStatus.LOADING } });

    return this.taxPaymentsApiService.getTaxPayments().pipe(
      tap({
        next: data => patchState({ taxPayments: { status: LoadableStatus.LOADED, data } }),
        error: error => patchState({ taxPayments: { status: LoadableStatus.ERROR, data: [], error } }),
      }),
      take(1),
    );
  }

  @Action([ClearTaxPayments])
  clearTaxPayments({ patchState }: StateContext<SettingsStateModel>): void {
    patchState({
      taxPayments: DEFAULT_LOADABLE_STATE,
    });
  }

  @Action([ResetState])
  resetState({ patchState }: StateContext<SettingsStateModel>): void {
    patchState(DEFAULT_SETTINGS_STATE_MODEL);
  }

  /**
   * Set opt out to true of billing zip is in opt out list.
   * @param paymentPlanUi
   * @param usStates
   */
  setOptOutForPaymentPlanUis(paymentPlanUi: PaymentPlanUi, usStates: UsState[]): PaymentPlanUi {
    if (
      paymentPlanUi.paymentPlan.paymentMethodId.includes('pm_')
      && areStringsEqualIgnoreCase(paymentPlanUi.paymentPlan.paymentMethod, CardFunding.CREDIT)
      && paymentPlanUi.paymentPlan.zip
    ) {
      const stateAbbrev = zipState(paymentPlanUi.paymentPlan.zip) as UsStateAbbreviation;
      paymentPlanUi.paymentPlan = { ...paymentPlanUi.paymentPlan, usState: stateAbbrev };

      paymentPlanUi.optOutOfProcessingFees = usStates.find(usState =>
        usState.stateAbbreviation === stateAbbrev,
      )?.optOutOfProcessingFee;
    }

    return paymentPlanUi;
  }

  /**
   * Sets date used calculate date range based of user dropdown selection.
   * @param value
   */
  static apiDateParameters(value): string {
    let endDate = moment();
    let returnString: string;
    if (value == 'Show - Next 3 Months') {
      returnString = endDate.add(3, 'month').format('YYYY-MM-DD');
    } else if (value == 'Show - Next 6 Months') {
      returnString = endDate.add(6, 'month').format('YYYY-MM-DD');
    } else if (value == 'Show - Next Year') {
      returnString = endDate.add(12, 'month').format('YYYY-MM-DD');
    } else {
      returnString = '';
    }
    return returnString;
  }

  static inferNextPaymentsForPlan(plan: PaymentPlanUi, scheduledDate: Date): HasuraScheduledPaymentModel[] {
    const nextDate: Date = SettingsState.calcNextDate(scheduledDate, plan.paymentPlan.frequency);
    // SOMETIMES, the date is a string -_-
    let endDate: Date;
    if (typeof plan.paymentPlan.endDate === 'string') {
      const endDateParts: string[] = (plan.paymentPlan.endDate as string).split('-');
      // Months in Javascript are 0 based when creating new dates
      endDate = new Date(+endDateParts[0], (+endDateParts[1] - 1), +endDateParts[2]);
    } else {
      endDate = plan.paymentPlan.endDate;
    }

    if (nextDate > endDate) {
      return [];
    }

    return [
      {
        name: plan.paymentPlan.name,
        amount: plan.paymentPlan.amount,
        maxAmount: plan.paymentPlan.amount,
        startDate: plan.paymentPlan.startDate,
        endDate: plan.paymentPlan.endDate,
        frequency: plan.paymentPlan.frequency,
        releaseDate: `${nextDate.getFullYear()}-${nextDate.getMonth() + 1}-${nextDate.getDate()}`,
        status: 'STAGED',
        // All payment plans are recurring payment plans
        type: 'RECURRING',
        emails: plan.paymentPlan.emailConfirmationAddressList,
        cardType: plan.paymentPlan.cardType,
        last4: plan.paymentPlan.last4,
        country: plan.paymentPlan.country,
        zip: plan.paymentPlan.zip,
        paymentMethodType: plan.paymentPlan.paymentMethod,
        platformPaymentMethodId: plan.paymentPlan.paymentMethodId,
        clientId: plan.client.id,
        clientName: plan.client.name,
        createdByContactId: plan.paymentPlan.createdByContactId?.valueOf(),
        // contactFullName: plan.contact.,
        createdByStaffId: plan.paymentPlan.createdByStaffId?.valueOf(),
        // staffFullName: '',
        optOutOfProcessingFees: plan.optOutOfProcessingFees,
      } as unknown as HasuraScheduledPaymentModel,
      ...SettingsState.inferNextPaymentsForPlan(plan, nextDate)
    ];
  }

  static calcNextDate(date: Date, frequency: string): Date {
    const nextDate: Date = new Date(date.getTime());

    switch (frequency.toUpperCase()) {
      case 'WEEKLY':
        nextDate.setDate(nextDate.getDate() + 7);
        break;
      case 'BIWEEKLY':
        nextDate.setDate(nextDate.getDate() + 14);
        break;
      case 'MONTHLY':
        nextDate.setMonth(nextDate.getMonth() + 1);
        break;
      case 'QUARTERLY':
        nextDate.setMonth(nextDate.getMonth() + 3);
        break;
      case 'YEARLY':
        nextDate.setMonth(nextDate.getMonth() + 12);
        break;
    }

    return nextDate;
  }
}
